import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { ClientsSpreadsheet, CLIENT_EXPORT_FIELDS } from "@/components/spreadsheet/clients-spreadsheet";
import { Loader2, Plus } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { BulkActionsDropdown } from "@/components/bulk-actions-dropdown";
import { ClientRecordDialog } from "@/components/spreadsheet/client-record-dialog";
import { queryClient } from "@/lib/queryClient";
import { useEffectivePermissions } from "@/hooks/use-effective-permissions";
import { useLocation, useSearch } from "wouter";
import { SmartSearchBar } from "@/components/smart-search-bar";
import { RecordSuggestionChips } from "@/components/record-suggestion-chips";
import { useGlobalSearchSuggestions } from "@/hooks/use-global-search-suggestions";
import { useIsMobile } from "@/hooks/use-mobile";
import { SpreadsheetSkeleton } from "@/components/spreadsheet-skeleton";
import type { NlFilterSpec, UserRole, Region, Talent } from "@shared/schema";

interface ClientsPageProps {
  effectiveRole?: UserRole;
  effectiveUserId?: string;
}

export default function ClientsPage({ effectiveRole, effectiveUserId }: ClientsPageProps) {
  const { user, isLoading } = useAuth();
  const [, navigate] = useLocation();
  const search = useSearch();
  const [searchQuery, setSearchQuery] = useState("");
  const [smartSpec, setSmartSpec] = useState<NlFilterSpec | null>(null);
  const [smartEnabled, setSmartEnabled] = useState(false);
  const [smartHydrated, setSmartHydrated] = useState(false);
  const [isNewClientDialogOpen, setIsNewClientDialogOpen] = useState(false);

  // Read auto-open params from URL — set both by lead conversion and
  // by the sidebar quick-find. We use `useSearch` (not `useLocation`)
  // because wouter's useLocation only returns the pathname, so a
  // same-page navigation that only changes the query string would
  // otherwise not re-render this component.
  const urlParams = new URLSearchParams(search);
  const autoOpenClientId = urlParams.get("openClient") || undefined;
  const initialRegion = urlParams.get("region") || undefined;

  const handleAutoOpenComplete = () => {
    navigate("/clients", { replace: true });
  };

  const { data: profile } = useQuery<{ role: string; smartSearchEnabled?: boolean }>({
    queryKey: ["/api/profile"],
    enabled: !!user,
  });
  const { data: aiBalance } = useQuery<{ enabled?: boolean }>({
    queryKey: ["/api/ai/balance"],
    enabled: !!user,
  });
  const aiEnabledForWorkspace = !!aiBalance?.enabled;
  useEffect(() => {
    if (smartHydrated) return;
    if (typeof profile?.smartSearchEnabled === "boolean") {
      setSmartEnabled(profile.smartSearchEnabled);
      setSmartHydrated(true);
    }
  }, [profile?.smartSearchEnabled, smartHydrated]);

  const { data: regions = [] } = useQuery<Region[]>({
    queryKey: ["/api/regions"],
    enabled: !!user,
  });

  const { data: talent = [] } = useQuery<Talent[]>({
    queryKey: ["/api/talent"],
    enabled: !!user,
  });

  const isAdmin = effectiveRole ? effectiveRole === "admin" : profile?.role === "admin";
  
  // Permission checking
  const { can } = useEffectivePermissions();
  const canBulkActions = can("clients.feature.bulkActions");
  const isMobile = useIsMobile();

  // Trigram "Did you mean…?" suggestions for the in-page search input.
  // Mirrors the sidebar quick-find behaviour (Task #293) — when fewer
  // than 3 clients match the typed query, surface the same chip row so a
  // single typo still earns a correction prompt. Reuses the shared
  // /api/search/global hook so the React Query cache is shared across
  // every search input that hits the same query string.
  // Track the *typed* draft separately from the committed
  // `searchQuery` so the chip row reacts mid-typing without requiring
  // Enter/blur. `searchQuery` still only changes on Enter/blur so the
  // table-level filter behaviour is unchanged.
  const [searchDraft, setSearchDraft] = useState("");
  const { data: globalSuggestions } = useGlobalSearchSuggestions(
    searchDraft,
    250,
  );
  const clientHitCount = globalSuggestions?.items?.clients.length ?? 0;
  const suggestionTerms = globalSuggestions?.suggestions ?? [];
  const showSuggestions =
    searchDraft.trim().length > 0 &&
    clientHitCount < 3 &&
    suggestionTerms.length > 0;

  if (isLoading) {
    return <SpreadsheetSkeleton />;
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">Please sign in to view clients.</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="p-3 sm:p-6 pb-0 flex flex-col sm:flex-row sm:flex-wrap sm:items-start sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold">Clients</h1>
          <p className="text-sm text-muted-foreground hidden sm:block">
            View and manage booked clients. Single-click to select a row, double-click to edit.
          </p>
        </div>
        {canBulkActions && !isMobile && (
          <BulkActionsDropdown
            entityType="clients"
            dataEndpoint="/api/clients"
            fields={CLIENT_EXPORT_FIELDS}
            size="sm"
            onImportComplete={() => queryClient.invalidateQueries({ queryKey: ["/api/clients"] })}
            enrichmentData={{ regions, talent }}
          />
        )}
      </div>
      {showSuggestions && (
        <div className="px-3 sm:px-6 mt-2">
          <RecordSuggestionChips
            suggestions={suggestionTerms}
            onSelect={(term) => {
              // Update both the typed draft (so the suggestions hook
              // re-queries with the corrected term and the chip row
              // hides if it now yields ≥3 hits) and the committed
              // searchQuery (so the table filter applies).
              setSearchQuery(term);
              setSearchDraft(term);
            }}
            containerTestId="suggestions-clients-search"
            buttonTestIdPrefix="button-clients-suggestion"
          />
        </div>
      )}
      <div className="flex-1 overflow-hidden mt-2 sm:mt-4">
        <ClientsSpreadsheet
          userId={user.id}
          searchQuery={searchQuery}
          autoOpenClientId={autoOpenClientId}
          initialRegion={initialRegion}
          onAutoOpenComplete={handleAutoOpenComplete}
          smartFilters={smartSpec}
          savedViewsRightSlot={
            <SmartSearchBar
              page="clients"
              searchQuery={searchQuery}
              onSearchQueryChange={setSearchQuery}
              onDraftChange={setSearchDraft}
              spec={smartSpec}
              onSpecChange={setSmartSpec}
              smartEnabled={smartEnabled}
              onSmartEnabledChange={setSmartEnabled}
              aiEnabledForWorkspace={aiEnabledForWorkspace}
              placeholder="Search clients..."
              regions={regions}
            />
          }
        />
      </div>

      <ClientRecordDialog
        isOpen={isNewClientDialogOpen}
        onClose={() => {
          setIsNewClientDialogOpen(false);
          queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
        }}
        client={null}
        regions={regions}
        userId={user.id}
      />
    </div>
  );
}
